"use client";

import {
  FC,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "next/navigation";
import Decimal from "decimal.js";
import {
  ISubscriptionOverview,
  TFreshMealPlan,
  SubscriptionsAPI,
} from "@api-clients/subscriptions";
import { IStoryBlokComponent } from "apps/website/components/page/api.types";
import { useAuth } from "@auth/client-sdk-react";
import { PublicConfig } from "apps/website/config/public";
import {
  checkXmasRecipeEligibility,
} from "apps/website/utils/website/xmas-recipe-eligibilty";
import { useAPI } from "apps/website/hooks/useAPI";
import { CatsAPI } from "@api-clients/cats";
import { getWholeOrDecimal } from "apps/website/utils/subscriptions/plans";

import SectionWarningCard from "../../section/SectionWarningCard/SectionWarningCard";
import SectionCheckboxEnabledCTA from "../../section/SectionCheckboxEnabledCTA/SectionCheckboxEnabledCTA";
import {
  TPrecancellationSubscriptionState,
} from "../../section/SectionSplash/SectionPreCancellationSplash/SectionPreCancellationSplash";

export interface IStoryBlokWarningCard {
  title: string;
  subscription_state?: TPrecancellationSubscriptionState;
}

interface IWarningBlock extends Omit<IStoryBlokWarningCard, "subscription_state"> {
  accessToken?: string;
  subscriptionId: string | null;
}

const getMatchingSubscription = (
  userSubscriptions: ISubscriptionOverview,
  subscriptionId: string,
): TFreshMealPlan | undefined => userSubscriptions?.freshMealPlans
  .find((subscription) => subscription.id === Number(subscriptionId));

const getDiscountText = (discountType: string, value: string): string => {

  if (discountType === "ABSOLUTE_TOTAL") {
    return `£${getWholeOrDecimal(value)}`;
  }
  if (discountType === "PERCENTAGE") {
    return `${new Decimal(value).toFixed(2).replace(/\.?0+$/, "")}%`;
  }
  return `£${getWholeOrDecimal(value)}`;
};

/**
 * Determines the appropriate warning card content based on user state
 */
const getWarningCardContent = (
  title: string,
  matchingSubscription: TFreshMealPlan | undefined,
  subscriptionId: string | null,
  eligibleForXmasRecipe: boolean,
  eligibleForToteBag: boolean,
): { title: string; body: string } | null => {
  if (!subscriptionId || !matchingSubscription) {
    return null;
  }

  const discount = matchingSubscription.pricing.discounts.at(-1);
  const hasDiscount = !!discount;

  // Xmas recipe takes priority
  if (eligibleForXmasRecipe) {
    return {
      title: "Don’t miss out on your special festive surprise!",
      body: "You have a FREE special-edition recipe in your next box, and you’ll lose it if you cancel.",
    };
  }

  // Discount + tote bag combination
  if (hasDiscount && eligibleForToteBag && discount) {
    const discountText = discount.type === "ABSOLUTE_TOTAL"
      ? `discounted price of £${discount.amount}`
      : `${getDiscountText(discount.type, discount.amount)} off`;

    return {
      title,
      body: `You'll get your next box at a ${discountText} AND a surprise gift in your next box. You'll lose them if you cancel.`,
    };
  }

  // Tote bag only
  if (eligibleForToteBag && !hasDiscount) {
    return {
      title,
      body: "You have a surprise gift in your next box, and you'll lose it if you cancel.",
    };
  }

  // Discount only
  if (hasDiscount && !eligibleForXmasRecipe && discount) {
    const discountText = discount.type === "ABSOLUTE_TOTAL"
      ? `discounted price of £${discount.amount}`
      : `${getDiscountText(discount.type, discount.amount)} off your next box`;

    return {
      title,
      body: `You'll get your next box at a ${discountText}, and you'll lose the offer if you cancel.`,
    };
  }

  return null;
};

const WarningBlock: FC<IWarningBlock> = ({ title, accessToken, subscriptionId }) => {

  const [ matchingSubscription, setMatchingSubscription ] = useState<TFreshMealPlan>();
  const [ eligibleForXmasRecipe, setEligibleForXmasRecipe ] = useState<boolean>(false);
  const [ eligibleForToteBag, setEligibleForToteBag ] = useState<boolean>(false);
  const { Orders } = useAPI();

  useEffect(() => {
    if (accessToken) {
      const effectSetSubscriptionsAndXmasState = async () => {
        try {
          if (!subscriptionId) {
            return;
          }

          // Reason we aren't using useApi with this library is that historically it
          // returned a 401, when we tried to call Customer api.
          // This might be fixed now that we're caling SubscriptionsAPI
          // But it's out of scope of the current work to test this.
          const api = new SubscriptionsAPI(
            PublicConfig.NEXT_PUBLIC_SUBSCRIPTIONS_BASE_URL,
            accessToken,
            { withCredentials: false },
          );

          const subscriptionsOverview = await api.getSubscriptionsOverview();

          const sub = getMatchingSubscription(subscriptionsOverview, subscriptionId);
          setMatchingSubscription(sub);

          const Cats = new CatsAPI(
            PublicConfig.NEXT_PUBLIC_CATS_BASE_URL,
            accessToken,
            { withCredentials: false },
          );

          const onBoxTwo = sub?.fulfillment.chargeCount === 2;
          setEligibleForToteBag(onBoxTwo);
          const isEligibleForXmasRecipe = await checkXmasRecipeEligibility(
            Cats,
            Orders,
            +subscriptionId,
            subscriptionsOverview,
          );

          setEligibleForXmasRecipe(isEligibleForXmasRecipe);
        } catch (error) {
          console.error("Error figuring out if to show warning card", error);
        }
      };
      void effectSetSubscriptionsAndXmasState();
    }
  }, [ accessToken, subscriptionId ]);

  const warningCardContent = useMemo(() => getWarningCardContent(
    title,
    matchingSubscription,
    subscriptionId,
    eligibleForXmasRecipe,
    eligibleForToteBag,
  ), [ title, matchingSubscription, subscriptionId, eligibleForXmasRecipe, eligibleForToteBag ]);

  return (
    <>
      { warningCardContent && (
        <SectionWarningCard
          title={warningCardContent.title}
          body={warningCardContent.body}
        />
      ) }
    </>
  );
};

const StoryBlokWarningCardChild: FC<IStoryBlokComponent<IStoryBlokWarningCard>> = ({ blok }) => {
  const { accessToken } = useAuth();
  const searchParams = useSearchParams();

  return (
    <>
      <WarningBlock title={blok.title} accessToken={accessToken} subscriptionId={searchParams.get("subscriptionId")} />
      <SectionCheckboxEnabledCTA></SectionCheckboxEnabledCTA>
    </>
  );
};

export const StoryBlokWarningCard: FC<IStoryBlokComponent<IStoryBlokWarningCard>> = ({ blok }) => (
  <Suspense>
    <StoryBlokWarningCardChild blok={blok} />
  </Suspense>
);
